export default function HowWeWork() {
  const steps = [
    {
      number: '01',
      icon: 'ri-line-chart-line',
      title: 'Digitization and Analytics',
      subtitle: 'You can\'t manage what you can\'t measure',
      description: 'First step — implementing reporting standards',
      features: [
        'Setting up counters and goals',
        'Implementing end-to-end analytics',
        'Marking all traffic (UTM tags) to control the effectiveness of each ad'
      ],
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'from-cyan-50 to-blue-50',
      borderColor: 'border-cyan-200'
    },
    {
      number: '02',
      icon: 'ri-route-line',
      title: 'Sales Funnel Design',
      subtitle: 'We don\'t just "drive traffic to the site"',
      description: 'We build a thoughtful customer journey',
      features: [
        'Dividing traffic into "cold", "warm" and "hot"',
        'Creating landing pages for specific audience segments',
        'Implementing auto-funnels to warm up those who are not ready to buy immediately'
      ],
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200'
    },
    {
      number: '03',
      icon: 'ri-flask-line',
      title: 'Hypothesis Testing',
      subtitle: 'Marketing is mathematics, not magic',
      description: 'HADI cycles for continuous improvement',
      features: [
        'We put forward a hypothesis ("this headline will increase conversion")',
        'Quickly test on a real audience',
        'Measure the result and implement or discard'
      ],
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-200'
    },
    {
      number: '04',
      icon: 'ri-rocket-line',
      title: 'Scaling',
      subtitle: 'When a working combination is found',
      description: '"Channel — Offer — Profit"',
      features: [
        'Scale the budget to proven channels',
        'The system allows you to grow exponentially (x2, x3, x5)',
        'Maintain profitability while increasing volumes'
      ],
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200'
    },
    {
      number: '05',
      icon: 'ri-robot-2-line',
      title: 'Automation',
      subtitle: 'Transferring routine to robots and scripts',
      description: 'Freeing up time for strategy',
      features: [
        'Automatic reports and notifications',
        'Robotization of repetitive processes',
        'Team focus on strategic tasks, not routine'
      ],
      color: 'from-pink-500 to-red-500',
      bgColor: 'from-pink-50 to-red-50',
      borderColor: 'border-pink-200'
    }
  ];

  const benefits = [
    {
      icon: 'ri-eye-line',
      title: 'See real income',
      description: 'ROI/ROMI from every ruble invested'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Decisions based on numbers',
      description: 'Not intuition or guesswork'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Predictable conveyor',
      description: 'Without dependence on "star" employees'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full mb-6">
            <span className="font-bold text-lg">🔄 Transition from chaos to system</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            How We Work
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            5 stages of building a system — a step-by-step process of transforming your marketing
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-8 mb-20">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${step.bgColor} rounded-3xl p-8 md:p-10 border-2 ${step.borderColor} hover:shadow-2xl transition-all duration-300 group`}
            >
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Left: Number & Icon */}
                <div className="flex-shrink-0">
                  <div className={`w-24 h-24 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-xl`}>
                    <i className={`${step.icon} text-4xl text-white`}></i>
                  </div>
                  <div className={`text-6xl font-black bg-gradient-to-br ${step.color} bg-clip-text text-transparent opacity-30`}>
                    {step.number}
                  </div>
                </div>

                {/* Right: Content */}
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-2">
                    <strong>{step.subtitle}</strong>
                  </p>
                  <p className="text-gray-600 mb-6">
                    {step.description}
                  </p>

                  <div className="space-y-3">
                    {step.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                          <i className="ri-check-line text-green-600 text-sm font-bold"></i>
                        </div>
                        <span className="text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Block */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-3xl p-10 md:p-16 text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="text-center mb-12">
              <div className="inline-block px-6 py-2 bg-red-500/20 border border-red-500/50 rounded-full mb-6">
                <span className="text-red-300 font-semibold">⚠️ Stop wasting money on ineffective tools</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                A systematic approach allows the business owner to:
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${benefit.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold mb-2">{benefit.title}</h4>
                  <p className="text-gray-300">{benefit.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-2 border-cyan-400/50 rounded-2xl p-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <i className="ri-shield-check-line text-4xl text-cyan-400"></i>
                <h4 className="text-2xl font-bold">We don't sell "ad setup"</h4>
              </div>
              <p className="text-xl text-cyan-100">
                But <strong className="text-white">"building a system"</strong> = Strategy + Tools + Team
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
